<template>
    <vx-table-row :disabled="props.modelValue.status === 'archived'">
        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <font-awesome-icon widthAuto class="mr-2" :class="status.color" :icon="faCircle" />
            {{ status.title }}
        </vx-table-row-item>

        <vx-table-row-item class="xl:col-span-2!">
            <template v-slot:label> Name: </template>
            <vx-popover
                :hover="true"
                :arrow="true"
                @click.prevent="copyText(props.modelValue.import_id)"
                placement="top"
                class="min-w-[unset]!"
            >
                <vx-badge class="cursor-pointer mr-2" size="xs" color="slate">
                    {{ props.modelValue.import_id.slice(-6) }}
                </vx-badge>

                <template v-slot:content>
                    {{ props.modelValue.import_id }}
                </template>
            </vx-popover>
            <vx-popover
                :hover="!!props.modelValue.file_name"
                :arrow="true"
                placement="top"
                class="[&>div:first-child]:truncate w-full"
            >
                {{ props.modelValue.file_name || '-' }}

                <template v-slot:content>
                    <div class="break-all">{{ props.modelValue.file_name }}</div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Rows: </template>
            <vx-popover :hover="true" :arrow="true" placement="top">
                {{ formatCount(props.modelValue.row_count) || '-' }}

                <template v-slot:content>
                    {{ Intl.NumberFormat('en-US').format(props.modelValue.row_count) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item data-test="import-errors">
            <template v-slot:label> Errors: </template>
            <vx-icon-loading v-if="loadingErrors" spin></vx-icon-loading>
            <vx-popover v-else-if="props.modelValue.status === 'archived'" :hover="true" :arrow="true" placement="top">
                -
                <template v-slot:content> We do not retain error data beyond 60 days. </template>
            </vx-popover>
            <vx-badge
                v-else-if="errorsCount && errorsCount > 0"
                @click.prevent="selectedImport = props.modelValue"
                class="cursor-pointer"
                size="xs"
                color="rose"
            >
                {{ errorsCountTitle }}
                <font-awesome-icon widthAuto class="text-rose-500" :icon="faExclamationTriangle" />
            </vx-badge>
            <span v-else>{{ errorsCountTitle }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Created at: </template>

            <vx-popover hover :arrow="true" placement="top" :key="createdAtKey">
                {{ fromNow(props.modelValue.created_at) }}
                <template v-slot:content>
                    {{ shortDateTime(props.modelValue.created_at) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button
                v-if="errorsCount && errorsCount > 0"
                @click.prevent="selectedImport = props.modelValue"
                color="muted"
                outline
            >
                View <font-awesome-icon widthAuto :icon="faExclamationTriangle" />
            </vx-button>
        </vx-table-row-buttons>

        <import-errors-modal v-model="selectedImport"></import-errors-modal>
    </vx-table-row>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxIconLoading,
    VxBadge,
} from '@voxie/frontend-components';
import { fromNow, shortDateTime } from '~/utils/date';
import { formatCount } from '~/components/filters';
import { useInterval } from '@vueuse/core';
import { faCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { useCopyText } from '~/composables/useCopyText';
import { computed, onMounted, ref } from 'vue';
import { importsClient, groupImportsClient } from '~/services';
import ImportErrorsModal from '~/components/import/ImportErrorsModal.vue';

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const selectedImport = ref();

const loadingErrors = ref(false);
const errorsCount = ref();
const errorsCountTitle = computed(() => {
    return errorsCount.value === 100 ? '99+' : errorsCount.value === undefined ? '-' : errorsCount.value;
});
const createdAtKey = useInterval(60000);
const { copyText } = useCopyText();

const status = computed(() => {
    switch (props.modelValue.status) {
        case 'complete':
            return { title: 'Completed', color: 'text-sky-500' };
        case 'in_progress':
            return { title: 'In Progress', color: 'text-amber-500' };
        case 'archived':
            return { title: 'Archived', color: 'text-slate-600' };
        default:
            return { title: 'Pending', color: 'text-slate-300' };
    }
});

const fetchImportErrorsCount = async () => {
    if (!['in_progress', 'complete'].includes(props.modelValue.status) || loadingErrors.value) {
        return;
    }

    loadingErrors.value = true;

    const params = { limit: 100 };

    try {
        const response = props.modelValue.group_id
            ? await groupImportsClient.listErrors(
                  props.modelValue.team_id,
                  props.modelValue.group_id,
                  props.modelValue.import_type,
                  props.modelValue.import_id,
                  params
              )
            : await importsClient.listErrors(
                  props.modelValue.team_id,
                  props.modelValue.import_type,
                  props.modelValue.import_id,
                  params
              );

        errorsCount.value = response.data?.data.length;
    } finally {
        loadingErrors.value = false;
    }
};

onMounted(() => {
    fetchImportErrorsCount();
});
</script>
